// Typing a passcode, for the lock screen, the lock over the till and the
// manager's approval sheet (#379).
//
// The lock screen and the lock take the same eight digits to the same
// endpoint; the approval sheet sends them with the one operation it names.
// All three draw the same states, the ones the design system gives
// PasscodeDots, so the states live here once. The screen says what an
// accepted passcode means to it, and how its digits are sent.
//
// The digits belong to one picked cashier. Nothing can be typed until a name
// is picked, and picking another name drops what was typed, because those
// digits were meant for someone else.
//
//   idle      digits arriving.
//   checking  eight typed and sent; the pad waits for the answer.
//   error     refused, or no answer. The dots hold until the next digit,
//             which starts over as digit one, so a cashier who looks up late
//             still sees what happened.
//   locked    the server would not check at all. The pad waits out the time
//             the server gave, and the screen shows it counting down.

import { api } from '../api/client';
import { message, type Language } from '../i18n/messages';
import type { components } from '../api/types';
import type { Cashier } from './roster.svelte';

type Session = components['schemas']['SessionBody'];

export const PASSCODE_LENGTH = 8;

/** How long a check runs before the screen says it is checking. */
const SLOW_MS = 300;

export type PasscodePhase = 'idle' | 'checking' | 'error' | 'locked';

/**
 * Why the last try failed. `locked` is a lockout the server gave no time
 * for. The approval sheet's own: `internet`, the store could not reach the
 * server to check it; `forbidden`, the one picked may not approve; `refused`,
 * the operation itself was refused.
 */
export type PasscodeFailure =
	'wrong' | 'locked' | 'unreachable' | 'internet' | 'forbidden' | 'refused';

/** What sending the digits gave: the value on success, or why not. */
export type PasscodeResult<T> =
	| { ok: true; value: T }
	| { ok: false; failure: PasscodeFailure; lockSeconds?: number; wholeRestaurant?: boolean };

/** Sends the digits typed for the one picked. */
export type PasscodeSubmit<T> = (userId: string, passcode: string) => Promise<PasscodeResult<T>>;

/** The unlock: POST /api/till/sessions, and what each answer means to a lock. */
const unlock: PasscodeSubmit<Session> = async (userId, passcode) => {
	try {
		const { data, error, response } = await api.POST('/api/till/sessions', {
			body: { userId, passcode },
		});
		if (error === undefined && data) return { ok: true, value: data };
		if (response.status === 423) {
			return {
				ok: false,
				failure: 'locked',
				lockSeconds: retryAfterSeconds(error?.details),
				wholeRestaurant: error?.details?.scope === 'account',
			};
		}
		return { ok: false, failure: response.status >= 500 ? 'unreachable' : 'wrong' };
	} catch {
		return { ok: false, failure: 'unreachable' };
	}
};

export class PasscodeEntry<T = Session> {
	/** Who the digits are for. The pad shows only once someone is picked. */
	picked = $state<Cashier | null>(null);
	digits = $state('');
	phase = $state<PasscodePhase>('idle');
	failure = $state<PasscodeFailure>('wrong');
	/** A check has run past SLOW_MS. */
	slow = $state(false);
	/** Seconds until the lock lifts, while locked. */
	secondsLeft = $state(0);
	/** The lockout is the whole restaurant's, not this till's. */
	wholeRestaurant = $state(false);

	#onUnlocked: (value: T) => void;
	#submit: PasscodeSubmit<T>;
	#slowTimer: ReturnType<typeof setTimeout> | undefined;
	#lockTimer: ReturnType<typeof setInterval> | undefined;

	constructor(onUnlocked: (value: T) => void, submit?: PasscodeSubmit<T>) {
		this.#onUnlocked = onUnlocked;
		this.#submit = submit ?? (unlock as unknown as PasscodeSubmit<T>);
	}

	/** The pad takes keys. */
	get open(): boolean {
		return this.phase === 'idle' || this.phase === 'error';
	}

	/** Backspace has something to take: a digit, or a refused code to clear. */
	get canDelete(): boolean {
		return this.phase === 'error' || (this.phase === 'idle' && this.digits.length > 0);
	}

	/** How many dots are full. A refused code keeps all of them. */
	get filled(): number {
		return this.phase === 'error' ? PASSCODE_LENGTH : this.digits.length;
	}

	/** The state PasscodeDots draws. Checking is full dots and a waiting pad. */
	get dots(): 'idle' | 'error' | 'locked' {
		return this.phase === 'checking' ? 'idle' : this.phase;
	}

	/**
	 * Picks the cashier the next digits are for. A lockout holds whoever is
	 * picked: it is on this till, not on a name.
	 */
	pick(cashier: Cashier): void {
		if (this.phase === 'checking') return;
		this.picked = cashier;
		this.#dropDigits();
	}

	/** Back to the names, dropping whatever was typed for the last one. */
	unpick(): void {
		if (this.phase === 'checking') return;
		this.picked = null;
		this.#dropDigits();
	}

	type(key: string): void {
		if (!this.open || this.picked === null) return;
		const next = this.phase === 'error' ? key : this.digits + key;
		this.phase = 'idle';
		this.digits = next;
		if (next.length === PASSCODE_LENGTH) void this.#check(next);
	}

	backspace(): void {
		if (this.phase === 'error') {
			this.digits = '';
			this.phase = 'idle';
		} else if (this.phase === 'idle') {
			this.digits = this.digits.slice(0, -1);
		}
	}

	/** Stops the timers. The screen calls it when it goes away. */
	stop(): void {
		clearTimeout(this.#slowTimer);
		clearInterval(this.#lockTimer);
	}

	#dropDigits(): void {
		this.digits = '';
		if (this.phase !== 'locked') this.phase = 'idle';
	}

	async #check(passcode: string): Promise<void> {
		const userId = this.picked?.id ?? '';
		this.phase = 'checking';
		this.#slowTimer = setTimeout(() => (this.slow = true), SLOW_MS);
		try {
			const result = await this.#submit(userId, passcode);
			if (result.ok) {
				this.digits = '';
				this.phase = 'idle';
				this.#onUnlocked(result.value);
				return;
			}
			if (result.failure === 'locked') {
				this.wholeRestaurant = result.wholeRestaurant ?? false;
				const seconds = result.lockSeconds ?? 0;
				if (seconds > 0) this.#lock(seconds);
				else this.#fail('locked');
			} else {
				this.#fail(result.failure);
			}
		} catch {
			this.#fail('unreachable');
		} finally {
			clearTimeout(this.#slowTimer);
			this.slow = false;
		}
	}

	#fail(failure: PasscodeFailure): void {
		this.digits = '';
		this.failure = failure;
		this.phase = 'error';
	}

	// Counted against the clock rather than by ticks, so a slow tick cannot
	// stretch the wait.
	#lock(seconds: number): void {
		const until = Date.now() + seconds * 1000;
		this.digits = '';
		this.phase = 'locked';
		this.secondsLeft = seconds;
		clearInterval(this.#lockTimer);
		this.#lockTimer = setInterval(() => {
			const left = Math.ceil((until - Date.now()) / 1000);
			if (left > 0) {
				this.secondsLeft = left;
				return;
			}
			clearInterval(this.#lockTimer);
			this.secondsLeft = 0;
			this.phase = 'idle';
		}, 250);
	}
}

/** A lock's wait, in whole seconds, from a refusal's details; 0 with none. */
export function retryAfterSeconds(details: Record<string, unknown> | undefined): number {
	const value = details?.retry_after_seconds;
	return typeof value === 'number' && Number.isFinite(value) ? Math.ceil(value) : 0;
}

/** What the line under the dots says. Empty while there is nothing to say. */
export function passcodeMessage<T>(entry: PasscodeEntry<T>, language: Language): string {
	switch (entry.phase) {
		case 'checking':
			return entry.slow ? message(language, 'lockChecking') : '';
		case 'locked':
			return entry.wholeRestaurant
				? message(language, 'lockAccountLocked')
				: `${message(language, 'lockLockedFor')} ${formatWait(entry.secondsLeft)}`;
		case 'error':
			if (entry.failure === 'locked') {
				return entry.wholeRestaurant
					? message(language, 'lockAccountLocked')
					: message(language, 'lockLocked');
			}
			if (entry.failure === 'unreachable') return message(language, 'loadFailed');
			if (entry.failure === 'internet') return message(language, 'approvalInternet');
			if (entry.failure === 'forbidden') return message(language, 'approvalManagerOnly');
			if (entry.failure === 'refused') return message(language, 'ordersFailed');
			return message(language, 'lockWrong');
		default:
			return '';
	}
}

/** A wait as the lock shows it, minutes and seconds: 0:30, 4:59. */
export function formatWait(seconds: number): string {
	const whole = Math.max(0, Math.ceil(seconds));
	return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
